import type { FailMode, FailRule, FailStateHandling } from "@storyai/shared";
import { Field } from "./ui";

const MODE_LABEL: Record<FailMode, string> = {
  restart_chapter: "Restart the current chapter",
  restart_story: "Restart the whole story",
  goto_chapter: "Go to a specific chapter",
};

export function FailRuleEditor({
  value,
  onChange,
  chapters,
  disabled,
}: {
  value: FailRule;
  onChange: (next: FailRule) => void;
  chapters: { id: string; title: string }[];
  disabled?: boolean;
}) {
  const set = (patch: Partial<FailRule>) => onChange({ ...value, ...patch });

  return (
    <div className="fail-rule">
      <Field label="On failure">
        <select
          value={value.mode}
          disabled={disabled}
          onChange={(e) => {
            const mode = e.target.value as FailMode;
            set({ mode, targetChapterId: mode === "goto_chapter" ? value.targetChapterId ?? null : null });
          }}
        >
          {(Object.keys(MODE_LABEL) as FailMode[]).map((m) => (
            <option key={m} value={m}>
              {MODE_LABEL[m]}
            </option>
          ))}
        </select>
      </Field>

      {value.mode === "goto_chapter" && (
        <Field label="Target chapter">
          <select
            value={value.targetChapterId ?? ""}
            disabled={disabled}
            onChange={(e) => set({ targetChapterId: e.target.value || null })}
          >
            <option value="">— choose a chapter —</option>
            {chapters.map((c) => (
              <option key={c.id} value={c.id}>
                {c.title}
              </option>
            ))}
          </select>
        </Field>
      )}

      <fieldset className="radio-group">
        <legend className="field-label">Player state</legend>
        {(
          [
            ["rollback", "Roll back", "Inventory and flags return to what they were when the target chapter was first entered."],
            ["keep", "Keep", "The player keeps everything they picked up and every flag that was set."],
          ] as [FailStateHandling, string, string][]
        ).map(([v, label, desc]) => (
          <label key={v} className="radio">
            <input
              type="radio"
              name="fail-state"
              value={v}
              checked={value.state === v}
              disabled={disabled}
              onChange={() => set({ state: v })}
            />
            <span>
              <span className="radio-label">{label}</span>
              <span className="muted small"> {desc}</span>
            </span>
          </label>
        ))}
      </fieldset>

      <Field
        label="Message"
        hint="Optional. A hint the AI uses to narrate the failure."
      >
        <input
          type="text"
          value={value.message ?? ""}
          disabled={disabled}
          onChange={(e) => set({ message: e.target.value || null })}
          placeholder="e.g. The rail gives way beneath you…"
        />
      </Field>
    </div>
  );
}
